import { FC, forwardRef } from 'react'
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Slide,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import PhoneIcon from '@mui/icons-material/Phone'
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined'
import { TransitionProps } from '@mui/material/transitions'
import { THEME_COLOR } from '../AppDialog'
import {
  SUPPORT_PHONE_DISPLAY,
  openSupportPhoneCall,
  openSupportWhatsApp,
} from '../../utils/supportContact'

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

export interface TrialExpiredDialogProps {
  open: boolean
  onClose: () => void
}

export const TrialExpiredDialog: FC<TrialExpiredDialogProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme()

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          backgroundColor: theme.palette.background.paper,
        }}
      >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          bgcolor: THEME_COLOR,
          color: 'white',
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 700, flex: 1 }}>
         
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 64px)',
          px: { xs: 3, sm: 6, md: 10 },
          py: { xs: 4, sm: 6 },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 720,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              backgroundColor: alpha(theme.palette.error.main, 0.1),
              border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
            }}
          >
            <HourglassEmptyOutlinedIcon
              sx={{
                fontSize: 32,
                color: theme.palette.error.main,
              }}
            />
          </Box>

          <Typography
            variant="body1"
            component="p"
            sx={{
              color: theme.palette.error.main,
              fontWeight: 600,
              lineHeight: 1.5,
              mb: 5.5,
              fontSize: { xs: '1rem', sm: '1.1rem' },
            }}
          >
            Your plan is expired. Please contact us to buy a plan.
          </Typography>

         

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            justifyContent="center"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Button
              variant="contained"
              size="medium"
              startIcon={<WhatsAppIcon sx={{ fontSize: 20 }} />}
              onClick={() => openSupportWhatsApp()}
              sx={{
                whiteSpace: 'nowrap',
                px: 2.5,
                backgroundColor: '#25D366',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                '&:hover': {
                  backgroundColor: '#1da851',
                },
              }}
            >
              WhatsApp
            </Button>
            <Button
              variant="outlined"
              size="medium"
              startIcon={<PhoneIcon sx={{ fontSize: 20 }} />}
              onClick={() => openSupportPhoneCall()}
              sx={{
                whiteSpace: 'nowrap',
                px: 2.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                borderColor: alpha(theme.palette.primary.main, 0.45),
                color: theme.palette.primary.main,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.06),
                },
              }}
            >
              Call: {SUPPORT_PHONE_DISPLAY}
            </Button>
          </Stack>

          <Button
            variant="text"
            onClick={onClose}
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              fontSize: '0.875rem',
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
      </Box>
    </Dialog>
  )
}

export default TrialExpiredDialog
